import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { fmt, fmtPct, dirArrow } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLiveTickers } from "@/lib/useLiveTickers";
import { ThemeSwitcher } from "@/lib/theme";
import SignInDialog from "@/components/SignInDialog";
import FloatingChat from "@/components/FloatingChat";
import { toast } from "sonner";
import {
  LayoutDashboard, Bot, TelescopeIcon, LineChart, BarChart3, Sparkles, Briefcase,
  History, Database, Brain, Landmark, MessageSquare, ShieldCheck, CreditCard,
  Settings, Crown, Menu, ChevronLeft, Search, Command, Bell,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_SECTIONS = [
  {
    label: "OVERVIEW",
    items: [
      { to: "/",           label: "Command Center", icon: LayoutDashboard, tid: "nav-dashboard" },
      { to: "/agents",     label: "AI Agents",      icon: Bot,             tid: "nav-agents",   accent: "ai" },
      { to: "/research",   label: "AI Research",    icon: TelescopeIcon,   tid: "nav-research" },
      { to: "/chat",       label: "AI Chat",        icon: MessageSquare,   tid: "nav-chat" },
    ],
  },
  {
    label: "MARKETS",
    items: [
      { to: "/trade-ideas", label: "Trading Ideas", icon: Sparkles,   tid: "nav-trade-ideas", accent: "gold" },
      { to: "/chart",       label: "Charts",        icon: LineChart,  tid: "nav-chart" },
      { to: "/options",     label: "Options",       icon: BarChart3,  tid: "nav-options" },
      { to: "/altdata",     label: "Alt-Data",      icon: Database,   tid: "nav-altdata" },
    ],
  },
  {
    label: "PORTFOLIO",
    items: [
      { to: "/portfolio", label: "Portfolio Lab",  icon: Briefcase, tid: "nav-portfolio" },
      { to: "/backtest",  label: "Backtesting",    icon: History,   tid: "nav-backtest" },
      { to: "/brokers",   label: "Brokers",        icon: Landmark,  tid: "nav-brokers" },
      { to: "/memory",    label: "Memory / RAG",   icon: Brain,     tid: "nav-memory" },
    ],
  },
  {
    label: "ACCOUNT",
    items: [
      { to: "/security", label: "Security",   icon: ShieldCheck, tid: "nav-security" },
      { to: "/credits",  label: "Credits",    icon: CreditCard,  tid: "nav-credits", accent: "gold" },
    ],
  },
];

export default function Layout({ children }) {
  const loc = useLocation();
  const { user, loading, logout, refresh } = useAuth();
  const { tickers: liveTickers, isLive } = useLiveTickers();
  const [time, setTime] = useState(new Date());
  const [signInOpen, setSignInOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sth.sidebar.collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const avatarInputRef = useRef(null);

  const tickers = useMemo(() => {
    const arr = Object.values(liveTickers || {});
    return arr.sort((a, b) => (a.symbol || "").localeCompare(b.symbol || ""));
  }, [liveTickers]);

  useEffect(() => {
    if (!user) { setBalance(null); setIsAdmin(false); return; }
    let mounted = true;
    const load = async () => {
      try { const { data } = await api.get("/credits/balance"); if (mounted) setBalance(data); } catch { /* silent */ }
      try { const { data } = await api.get("/admin/whoami"); if (mounted) setIsAdmin(!!data.is_admin); } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, [user, loc.pathname]);

  useEffect(() => { localStorage.setItem("sth.sidebar.collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  useEffect(() => { const clk = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(clk); }, []);

  const pictureUrl = (u) => {
    if (!u?.picture) return null;
    return u.picture.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u.picture}` : u.picture;
  };

  const onAvatarPick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
    const fd = new FormData(); fd.append("file", f); fd.append("purpose", "avatar");
    try { await api.post("/files/upload", fd); await refresh(); toast.success("Avatar updated"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  const ticks = tickers.length ? [...tickers, ...tickers] : [];
  const sidebarWidth = collapsed ? "var(--sidebar-collapsed-width)" : "var(--sidebar-width)";

  return (
    <div className="app-shell min-h-screen" data-testid="app-shell">
      {/* SIDEBAR (desktop) */}
      <aside
        className="hidden lg:flex fixed top-0 left-0 h-screen flex-col glass-strong z-40 transition-[width] duration-300 ease-out"
        style={{ width: sidebarWidth }}
        data-testid="sidebar"
      >
        <SidebarInner collapsed={collapsed} isAdmin={isAdmin} onToggle={() => setCollapsed(v => !v)} />
      </aside>

      {/* SIDEBAR (mobile drawer) */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="w-[260px] h-full glass-strong flex flex-col">
            <SidebarInner collapsed={false} isAdmin={isAdmin} onToggle={() => setMobileOpen(false)} mobile />
          </div>
          <button className="flex-1 bg-black/50 backdrop-blur-sm" aria-label="Close menu" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* MAIN */}
      <div
        className="min-h-screen transition-[padding] duration-300 ease-out lg:pl-[var(--sth-sb-w)]"
        style={{ "--sth-sb-w": sidebarWidth }}
      >
        <div className="lg:hidden" style={{ paddingLeft: 0 }} />

        {/* TOP BAR */}
        <header className="sticky top-0 z-30 glass-strong border-b border-[color:var(--border-default)]" data-testid="app-header">
          <div className="flex items-center gap-3 px-4 md:px-6 h-16 min-w-0">
            <button
              className="lg:hidden btn-ghost !p-2 shrink-0"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn" aria-label="Open menu"
            >
              <Menu size={20} />
            </button>

            {/* Command search bar */}
            <button
              data-testid="cmd-k-trigger"
              onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
              className="flex-1 min-w-0 max-w-xl flex items-center gap-2.5 px-3.5 py-2 rounded-[10px] border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] hover:border-[color:var(--border-focus)] text-[color:var(--text-tertiary)] transition-colors group"
              title="Command palette · ⌘K"
            >
              <Search size={16} className="shrink-0" />
              <span className="text-sm truncate hidden sm:inline">Search symbols, agents, trades, or type a command…</span>
              <span className="text-sm truncate sm:hidden">Search…</span>
              <span className="ml-auto flex items-center gap-1 chip !py-0.5 !px-1.5 text-[10px] shrink-0">
                <Command size={11} /> K
              </span>
            </button>

            <div className="flex items-center gap-2 ml-auto">
              <div className="hidden md:flex items-center gap-1.5 text-xs font-mono-data" data-testid="live-ws-indicator">
                <span className={`w-2 h-2 rounded-full ${isLive ? "bg-[color:var(--bull)]" : "bg-[color:var(--bear)]"}`} />
                <span className="text-[color:var(--text-tertiary)]">{isLive ? "LIVE" : "OFF"}</span>
              </div>
              <div className="hidden md:block text-xs font-mono-data text-[color:var(--text-tertiary)]" data-testid="clock">
                {time.toUTCString().slice(17, 25)} UTC
              </div>
              <div className="hidden xl:block"><ThemeSwitcher /></div>

              {loading ? (
                <div className="w-24 h-9 skeleton" />
              ) : user ? (
                <>
                  {balance !== null && (
                    <Link to="/credits" data-testid="credit-balance-badge" className="chip chip-gold !py-1.5 !px-2.5 hidden md:inline-flex">
                      <span className="text-[color:var(--gold)] text-sm">◆</span>
                      <span className="font-mono-data font-semibold">{balance.balance}</span>
                      <span className="text-[10px] opacity-70">CR</span>
                    </Link>
                  )}
                  <button className="btn-ghost !p-2 hidden md:flex" aria-label="Notifications" data-testid="notifications-btn">
                    <Bell size={18} />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="profile-menu-trigger" className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-[color:var(--border-default)] hover:border-[color:var(--border-focus)] transition-colors">
                        {pictureUrl(user) ? (
                          <img src={pictureUrl(user)} alt="" className="w-8 h-8 rounded-full object-cover" data-testid="profile-picture" />
                        ) : (
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold" style={{ background: "var(--grad-purple)", color: "white" }}>
                            {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm max-w-[120px] truncate hidden md:inline text-[color:var(--text-primary)]" data-testid="profile-name">{user.name || user.email}</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[color:var(--bg-surface)] border-[color:var(--border-default)] min-w-[240px]" data-testid="profile-menu">
                      <DropdownMenuLabel className="text-xs text-[color:var(--text-tertiary)] font-mono-data">SIGNED IN AS</DropdownMenuLabel>
                      <div className="px-2 pb-2">
                        <div className="text-sm text-[color:var(--text-primary)] truncate">{user.name}</div>
                        <div className="text-xs text-[color:var(--text-secondary)] truncate font-mono-data">{user.email || user.phone}</div>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="upload-avatar-btn" onClick={() => avatarInputRef.current?.click()} className="cursor-pointer">
                        Change avatar
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer">
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <button data-testid="sign-in-btn" onClick={() => setSignInOpen(true)} className="btn btn-primary">
                  Sign In
                </button>
              )}
            </div>
          </div>

          {/* Scrolling ticker */}
          <div className="border-t border-[color:var(--border-default)] overflow-hidden h-10 flex items-center" data-testid="ticker-bar">
            {ticks.length === 0 ? (
              <div className="px-4 font-mono-data text-xs text-[color:var(--text-muted)]">Loading market feed…</div>
            ) : (
              <div className="flex whitespace-nowrap ticker-scroll">
                {ticks.map((t, i) => (
                  <div key={`${t.symbol}-${i}`} className="flex items-center gap-2.5 px-5 border-r border-[color:var(--border-subtle)]" data-testid={`ticker-${t.symbol}`}>
                    <span className="font-mono-data text-xs text-[color:var(--text-tertiary)]">{t.name}</span>
                    <span className="font-mono-data text-xs text-[color:var(--text-primary)] font-semibold">{fmt(t.price)}</span>
                    <span className={`font-mono-data text-xs ${t.change_pct >= 0 ? "text-bull" : "text-bear"}`}>
                      {dirArrow(t.change_pct)} {fmtPct(t.change_pct)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </header>

        <main className="min-h-[calc(100vh-6.5rem)] overflow-x-hidden" data-testid="main-content">
          {children}
        </main>

        <footer className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-xs text-[color:var(--text-muted)] font-mono-data border-t border-[color:var(--border-subtle)]">
          <span>SUPER TRADE HOUSE AI · MULTI-AGENT INSTITUTIONAL INTELLIGENCE</span>
          <span>NOT FINANCIAL ADVICE · PROBABILISTIC OUTPUTS · RISK MANAGEMENT REQUIRED</span>
        </footer>
      </div>

      <SignInDialog open={signInOpen} onOpenChange={setSignInOpen} />
      {loc.pathname !== "/chat" && <FloatingChat />}
      <input ref={avatarInputRef} type="file" accept="image/*" onChange={onAvatarPick} className="hidden" data-testid="avatar-file-input" />
    </div>
  );
}

function SidebarInner({ collapsed, isAdmin, onToggle, mobile }) {
  return (
    <>
      {/* Brand */}
      <div className="px-3 pt-4 pb-3 border-b border-[color:var(--border-subtle)] flex items-center gap-2.5">
        <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="brand-logo">
          <div className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: "var(--grad-purple)", boxShadow: "var(--shadow-glow-purple)" }}>
            <Crown size={18} color="white" strokeWidth={2.4} />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-serif-elite text-[17px] leading-tight tracking-tight text-[color:var(--text-primary)]">
                Super Trade House
              </div>
              <div className="text-[10px] font-mono-data tracking-widest gradient-text-gold font-semibold">
                AI · v0.1
              </div>
            </div>
          )}
        </Link>
        <button
          onClick={onToggle}
          className="ml-auto p-1.5 rounded-md hover:bg-[color:var(--bg-hover)] text-[color:var(--text-tertiary)] transition-colors"
          data-testid="sidebar-toggle" aria-label="Toggle sidebar"
        >
          {mobile ? <ChevronLeft size={16} /> : (collapsed ? <Menu size={16} /> : <ChevronLeft size={16} />)}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4" data-testid="main-nav">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label}>
            {!collapsed && (
              <div className="px-2.5 pb-1.5 text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
                {section.label}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map((n) => (
                <SidebarLink key={n.to} item={n} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
        {isAdmin && (
          <div>
            {!collapsed && <div className="px-2.5 pb-1.5 text-[10px] font-mono-data tracking-widest text-[color:var(--gold)]">ADMIN</div>}
            <SidebarLink item={{ to: "/admin", label: "Admin", icon: Settings, tid: "nav-admin", accent: "gold" }} collapsed={collapsed} />
          </div>
        )}
      </nav>

      {/* Footer status */}
      <div className="px-3 py-3 border-t border-[color:var(--border-subtle)]">
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2"}`}>
          <span className="dot-pulse" />
          {!collapsed && <span className="text-xs text-[color:var(--text-tertiary)]">30 Agents Online</span>}
        </div>
      </div>
    </>
  );
}

function SidebarLink({ item, collapsed }) {
  const { to, label, icon: Icon, tid, accent } = item;
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={tid}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `flex items-center gap-3 px-2.5 py-2.5 rounded-[10px] text-[13.5px] font-medium transition-all relative ${
          isActive
            ? "bg-[color:var(--bg-hover)] text-[color:var(--text-primary)]"
            : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)] hover:text-[color:var(--text-primary)]"
        } ${collapsed ? "justify-center" : ""}`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r" style={{ background: accent === "gold" ? "var(--gold)" : accent === "ai" ? "var(--ai)" : "var(--accent)" }} />}
          <Icon size={18} className={accent === "gold" ? "text-[color:var(--gold)]" : accent === "ai" ? "text-[color:var(--ai)]" : ""} strokeWidth={isActive ? 2.4 : 2} />
          {!collapsed && <span>{label}</span>}
        </>
      )}
    </NavLink>
  );
}
